import { CountryCodeEnum } from "@shared/enums/country.enum";
import { Injectable } from "@angular/core";
import { KeycloakService } from "keycloak-angular";
import { RoleEnum } from "@shared/enums/roles.enum";

@Injectable({
  providedIn: "root"
})
export class KeycloakHelperService {
  constructor(private keycloakService: KeycloakService) {}

  isBackofficeApproverManagerLogged(): boolean {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_APPROVER_MANAGER);
  }

  isBackofficeOperationLogged(): boolean {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_OPERATION);
  }

  isBackofficeExternalLogged(): boolean {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_EXTERNAL);
  }

  isBackofficeAdministratorLogged(): boolean {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.BACKOFFICE_ADMINISTRATOR);
  }

  isBackofficeUserLogged(): boolean {
    return (
      this.isBackofficeApproverManagerLogged() ||
      this.isBackofficeOperationLogged() ||
      this.isBackofficeExternalLogged() ||
      this.isBackofficeAdministratorLogged()
    );
  }
  isBackofficeUserLoggedExceptExternal(): boolean {
    return (
      this.isBackofficeApproverManagerLogged() ||
      this.isBackofficeOperationLogged() ||
      this.isBackofficeAdministratorLogged()
    );
  }

  isCarWorkshopLogged(): boolean {
    return this.keycloakService.getUserRoles().includes(RoleEnum.CAR_WORKSHOP);
  }

  isDriverLogged(): boolean {
    return this.keycloakService.getUserRoles().includes(RoleEnum.DRIVER_PERFIL);
  }

  isDriverApproverLogged(): boolean {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.DRIVER_APPROVER);
  }

  isFleetAdminNonApproverLogged(): boolean {
    return this.keycloakService.getUserRoles().includes(RoleEnum.FLEET_ADMIN);
  }

  isFleetAdminApproverLogged(): boolean {
    return this.keycloakService
      .getUserRoles()
      .includes(RoleEnum.FLEET_ADMIN_APPROVER);
  }

  isFleetAdminLogged(): boolean {
    return (
      this.isFleetAdminApproverLogged() || this.isFleetAdminNonApproverLogged()
    );
  }

  isBrazilian(): boolean {
    const userCountryCode = this.getUserCountryCode();
    return userCountryCode === CountryCodeEnum.BRAZIL;
  }

  isArgentinian(): boolean {
    const userCountryCode = this.getUserCountryCode();
    return userCountryCode === CountryCodeEnum.ARGENTINA;
  }

  isNorthAmerican(): boolean {
    const userCountryCode = this.getUserCountryCode();
    return userCountryCode === CountryCodeEnum.UNITED_STATES;
  }

  isNederland(): boolean {
    const userCountryCode = this.getUserCountryCode();
    return userCountryCode === CountryCodeEnum.NEDERLANDS;
  }

  getUserCountryCode(): string {
    return this.keycloakService.getKeycloakInstance().tokenParsed[
      "countryCode"
    ] as string;
  }

  getTypeUser(): string {
    const userRolesEnum = Object.values(RoleEnum);
    return userRolesEnum.find((role) => {
      return this.keycloakService.getUserRoles().includes(role);
    }) as string;
  }

  getUserRoles(): string[] {
    return this.keycloakService.getUserRoles();
  }

  getSessionState(): string | null {
    return (
      (this.keycloakService.getKeycloakInstance().tokenParsed?.[
        "session_state"
      ] as string) || null
    );
  }

  getSessionMarker(): string | null {
    const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
    const sessionState = tokenParsed?.["session_state"] as string | undefined;
    const authTime = tokenParsed?.["auth_time"] as number | undefined;

    if (sessionState) {
      return sessionState;
    }

    if (authTime) {
      return `${authTime}`;
    }

    return null;
  }

  logout(redirectUri?: string): Promise<void> {
    return this.keycloakService.logout(redirectUri);
  }

  getCarWorkshopId(): string {
    return this.keycloakService.getKeycloakInstance().tokenParsed[
      "carWorshopId"
    ] as string;
  }

  getPlatformId(): string {
    return this.keycloakService.getKeycloakInstance().tokenParsed[
      "smartfleet-platform-id"
    ] as string;
  }

  getUserRoleCategory(): string {
    const roleMap = {
      [RoleEnum.BACKOFFICE_OPERATION]: "backoffice",
      [RoleEnum.BACKOFFICE_ADMINISTRATOR]: "backoffice",
      [RoleEnum.BACKOFFICE_APPROVER_MANAGER]: "backoffice",
      [RoleEnum.BACKOFFICE_EXTERNAL]: "backoffice",
      [RoleEnum.FLEET_ADMIN_APPROVER]: "fleet_admin",
      [RoleEnum.FLEET_ADMIN]: "fleet_admin",
      [RoleEnum.CAR_WORKSHOP]: "car_workshop",
      [RoleEnum.DRIVER_PERFIL]: "driver",
      [RoleEnum.DRIVER_APPROVER]: "driver"
    };

    return (roleMap[this.getTypeUser()] as string) || "backoffice";
  }
}
